import React, {useCallback, useEffect, useMemo, useState} from 'react';
import dayjs from "dayjs";
import {DatePicker, Form, FormInstance, InputNumber, Radio} from "antd";
import styles from './EditForm.module.scss';
import GroupAPI from "../../../../../../../api/groupAPI";
import { ModalDataIF } from "../../../../../BaechaList2.interface";
import {BIG_CAR_TYPES, SMALL_CAR_TYPES, getPriceByType, getIsCanCallPass} from "../../../../../BaechaList2Util";

const getLabelComponent = (type: string, price: number) => (
	<div style={{ textAlign: 'center', height: 80 }}>
		<div>{type}</div>
		<div>{price / 10000}만 원</div>
	</div>
);

const formatter = (value?: number) => {
	if (value == null) return '';
	return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

interface EditFormProps {
	form: FormInstance<any>;
	modalData: ModalDataIF;
}

const EditForm = ({ form, modalData }: EditFormProps) => {
	const mhType = Form.useWatch('mhType', form);

	const [priceData, setPriceData] = useState<any[]>([]);
	const [config, setConfig] = useState({
		old: false,
		mid: false,
	});

	const { data, selectedBaechaWorkDayData } = modalData;
	const baechaCarType = data?.carType;

	const isSmallCar = !!baechaCarType && SMALL_CAR_TYPES.has(baechaCarType);
	const isBigCar   = !!baechaCarType && BIG_CAR_TYPES.has(baechaCarType);

	const getMhTypeOptions = () => {
		if (!baechaCarType) return [];

		const base = [
			{ label: '1시간', value: '1h' },
			{ label: '2시간', value: '2h' },
			{ label: '반나절', value: '4h' },
			{ label: '하루', value: '1d' },
		];

		if (['54m','58m','75m','28m','40m'].includes(baechaCarType)) return base.slice(2);
		if (['60m','70m'].includes(baechaCarType)) return [base[3]];

		return base;
	};

	// 현재 선택된 price target
	const currentPrice = useMemo(() => {
		return priceData.find(v => v.mhType === mhType);
	}, [mhType, priceData]);

	// price 옵션
	const priceOptions = useMemo(() => {
		if (!currentPrice) return [];

		const options = [];

		if (config.old) {
			options.push({
				label: getLabelComponent('구단가', currentPrice.oldPrice),
				value: currentPrice.oldPrice,
			});
		}

		if (config.mid) {
			options.push({
				label: getLabelComponent('중단가', currentPrice.midPrice),
				value: currentPrice.midPrice,
			});
		}

		options.push({
			label: getLabelComponent('신단가', currentPrice.newPrice),
			value: currentPrice.newPrice,
		});

		return options;
	}, [currentPrice, config]);

	const handleValuesChange = useCallback((changedValues: any, allValues: any) => {
		if (!('mhType' in changedValues)) return;

		const target = priceData.find(v => v.mhType === changedValues.mhType);
		if (!target) return;

		const price = getPriceByType(target, data?.priceType);

		if (allValues.price === price) return;

		form.setFieldValue('price', isBigCar ? 0 : price);
	}, [priceData, data?.priceType, isBigCar]);

	// fetch
	useEffect(() => {
		if (!data?.baljuingGroupId || !baechaCarType) return;

		(async () => {
			try {
				const res = await GroupAPI.getGroupConfigs(data.baljuingGroupId, ['price']);

				setConfig({
					old: res.config.enable_old_price,
					mid: res.config.enable_mid_price,
				});

				const newPriceData = res.price.filter((item: any) => {
					return getIsCanCallPass(item.carType, baechaCarType);
				});

				setPriceData(newPriceData);
			} catch (e) {
				console.error(e);
			}
		})();
	}, [data?.baljuingGroupId, baechaCarType]);


	// 초기값
	useEffect(() => {
		if (!selectedBaechaWorkDayData) return;

		const { workDate, mhType, price, overtimePrice } = selectedBaechaWorkDayData;

		form.setFieldsValue({
			workDate: dayjs(workDate),
			mhType,
			price: isBigCar ? price / 10000 : price,
			overtimePrice: overtimePrice ? overtimePrice / 10000 : 0,
		});
	}, [selectedBaechaWorkDayData, isBigCar]);

	return (
		<Form
			labelCol={{ sm: { span: 4 } }}
			wrapperCol={{ sm: { span: 18 } }}
			form={form}
			size="large"
			style={{ marginTop: 20 }}
			onValuesChange={handleValuesChange}
		>
			<Form.Item label="작업 날짜" name="workDate">
				<DatePicker allowClear={false} />
			</Form.Item>

			<Form.Item label="작업 시간" name="mhType">
				<Radio.Group
					block
					options={getMhTypeOptions()}
					optionType="button"
					buttonStyle="solid"
				/>
			</Form.Item>

			<Form.Item label="단가" name="price">
				{isSmallCar ? (
					<Radio.Group
						className={styles.priceRadioGroup}
						block
						options={priceOptions}
						optionType="button"
						buttonStyle="solid"
					/>
				) : (
					<InputNumber
						step={5}
						suffix="만 원"
						style={{ width: '100%' }}
						formatter={formatter}
					/>
				)}
			</Form.Item>

			<Form.Item label="야간 연장" name="overtimePrice">
				<InputNumber suffix="만 원" style={{ width: '100%' }} />
			</Form.Item>
		</Form>
	);
};

export default EditForm;
